import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Container, Typography, TextField, Grid, Button, Alert, CircularProgress,
  Card, CardContent, Chip, Divider, Dialog, DialogTitle, DialogContent, DialogActions,
  ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import RequireAuth from '../../components/athletes/RequireAuth';
import { useAuth } from '../../context/AuthContext';
import { getTeam, updateTeam, listTeamStaff, addTeamStaff, revokeTeamStaff } from '../../services/teamService';
import {
  hasProgramManagementAuthorityForTeam, updateTeamClassificationAndFootballType, archiveTeam, reactivateTeam,
  updateTeamPublicPageSettings, getProgram,
} from '../../services/programService';
import { roleLabel } from '../../utils/teamRoles';
import {
  CLASSIFICATION_TYPE_LABELS, NAMED_DIVISION_OPTIONS, NAMED_DIVISION_OTHER, FORMAT_OPTIONS,
  classificationDisplay, playersPerSideDisplay, footballTypeDisplay, formatTeamClassification,
} from '../../utils/teamClassification';
import { MultiSelectClassification, NamedDivisionClassification, FormatPicker, FootballTypePicker } from '../../components/teams/ClassificationPickers';

/** Team Admin management — moved here from the generic Staff page (see
 * TeamStaffPage.js's own comments) since Team Admin is a higher-
 * sensitivity Season-Team administration role, not an ordinary football-
 * operations one. Reuses add_team_staff()/revoke_team_staff()/
 * list_team_staff() (009) exactly as they exist — no dedicated Team
 * Admin RPC layer, no schema change; p_role is simply always
 * 'team_admin' from this form, and the list is filtered to that role
 * client-side. The existing last-Team-Admin safeguard (009) is enforced
 * server-side regardless of what this page does; the disabled-button
 * treatment below is a UX convenience mirroring that guarantee, not a
 * substitute for it.
 *
 * Clicking "Add" opens a confirmation dialog instead of granting
 * immediately — Team Admin is a high-authority Season-Team role, same
 * reasoning as Program Admin's own confirmation (ManageProgramPage.js).
 * Display name is deliberately NOT looked up or shown — add_team_staff()
 * resolves the target's profile and grants access atomically inside one
 * SECURITY DEFINER function, and there is no separate "look up a user by
 * email" RPC in this codebase (009's own comments document that as
 * deliberate). The confirmation shows the entered email only. */
function AddTeamAdminForm({ teamId, teamName, onAdded }) {
  const [email, setEmail] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    if (!email) { setError('Enter an email address.'); return; }
    setConfirmOpen(true); // no backend call yet
  };

  const handleConfirm = async () => {
    setSaving(true);
    try {
      const result = await addTeamStaff({ teamId, email, role: 'team_admin' });
      setSuccess(`${result.email || email} added as Team Admin.`);
      setEmail('');
      setConfirmOpen(false);
      await onAdded();
    } catch (e2) {
      setError(e2.message || 'Could not add Team Admin.');
      setConfirmOpen(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <TextField
          label="Email" type="email" size="small" value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder="admin@example.com"
          sx={{ flex: 1, minWidth: 220 }}
        />
        <Button type="submit" variant="contained" color="primary" sx={{ textTransform: 'none', fontWeight: 700, height: 40 }}>
          Add
        </Button>
        <Typography sx={{ color: '#4a6080', fontSize: '0.75rem', width: '100%' }}>
          They must already have a Football for Nerds account under this email.
        </Typography>
        {error && <Alert severity="error" sx={{ mt: 0.5, width: '100%' }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mt: 0.5, width: '100%' }}>{success}</Alert>}
      </Box>

      {/* Single, explicit confirmation — no typed confirmation. Positive/
          green action styling — this grants access, it isn't destructive. */}
      <Dialog
        open={confirmOpen}
        onClose={() => { if (!saving) setConfirmOpen(false); }}
        PaperProps={{ sx: { backgroundColor: 'rgba(10,20,34,0.98)', border: '1px solid #1e2a3a' } }}
      >
        <DialogTitle sx={{ fontWeight: 900 }}>Add Team Administrator?</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1.5 }}>You are about to give:</Typography>
          <Typography sx={{ fontWeight: 800, mb: 2 }}>{email}</Typography>

          <Typography sx={{ mb: 1.5 }}>Team Administrator access to:</Typography>
          <Typography sx={{ fontWeight: 800, mb: 2 }}>{teamName}</Typography>

          <Typography sx={{ color: '#9eb8d0', mb: 1.5 }}>
            They will be able to manage this Team, its roster/staff/settings, subject to existing backend authorization and season-state rules.
          </Typography>
          <Typography sx={{ color: '#9eb8d0' }}>
            This does not automatically grant Program Admin authority.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setConfirmOpen(false)} disabled={saving} sx={{ textTransform: 'none', color: '#9eb8d0' }}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={saving}
            variant="contained" color="primary"
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            {saving ? <CircularProgress size={18} sx={{ color: '#050c16' }} /> : 'Add Team Admin'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

function TeamSettingsInner() {
  const { teamId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [team, setTeam] = useState(null);
  const [form, setForm] = useState(null);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [revokingId, setRevokingId] = useState(null);
  const [revokeError, setRevokeError] = useState(null);

  // Program management authority (Owner or Admin) for THIS team — a
  // completely separate check from isTeamAdmin below (team_staff-based).
  // Classification/format editing (028) is Program-authority-gated only,
  // never team_admin/head_coach/assistant_coach, per the same authority
  // split TeamDetailPage's Setup-lifecycle controls already use.
  const [hasProgramAuthority, setHasProgramAuthority] = useState(false);

  // Phase C.3 — public Team page enablement (036's update_team_public_
  // page_settings(), Program management authority only — see that RPC's
  // own header for the product rule this preserves: a Team existing does
  // not require a public page). programSlug is fetched separately, only
  // when this Team is Program-linked, purely to render a "View Public
  // Page" link once enabled — never required for the toggle itself.
  const [publicPageSaving, setPublicPageSaving] = useState(false);
  const [publicPageError, setPublicPageError] = useState(null);
  const [programSlug, setProgramSlug] = useState(null);

  // Classification/format editing state (028) — initialized from the
  // loaded team, kept separate from `form` above since it saves through a
  // dedicated RPC (update_team_classification), not the plain updateTeam()
  // call the rest of this form uses.
  const [classificationType, setClassificationType] = useState(null);
  const [classificationValues, setClassificationValues] = useState([]);
  const [namedDivisionChoice, setNamedDivisionChoice] = useState('');
  const [namedDivisionOtherText, setNamedDivisionOtherText] = useState('');
  const [formatChoice, setFormatChoice] = useState('');
  const [formatOtherText, setFormatOtherText] = useState('');
  const [footballType, setFootballType] = useState('');
  const [originalClassification, setOriginalClassification] = useState(null);
  const [classificationSaving, setClassificationSaving] = useState(false);
  const [classificationError, setClassificationError] = useState(null);
  const [classificationConfirmOpen, setClassificationConfirmOpen] = useState(false);

  // Archive/Reactivate (028) — a single confirmation dialog for Archive
  // only; Reactivate never prompts. See handleArchiveButtonClick below.
  const [archiveConfirmOpen, setArchiveConfirmOpen] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [archiveError, setArchiveError] = useState(null);

  const loadStaff = useCallback(async () => {
    setStaff(await listTeamStaff(teamId));
  }, [teamId]);

  useEffect(() => {
    Promise.all([getTeam(teamId), listTeamStaff(teamId), hasProgramManagementAuthorityForTeam(teamId)])
      .then(([t, s, hasAuthority]) => {
        setTeam(t);
        setStaff(s);
        setHasProgramAuthority(hasAuthority);
        setForm({
          team_name: t.team_name,
          organization_name: t.organization_name || '',
          city: t.city,
          state: t.state,
          zip_code: t.zip_code,
          season: String(t.season),
          grade_age_group: t.grade_age_group,
        });

        // Reverse-map the loaded classification into picker-friendly UI
        // state — see resolveClassificationValues()/resolvePlayersPerSide()
        // below for the forward direction used on save.
        const type = t.classification_type || null;
        setClassificationType(type);
        if (type === 'named_division') {
          const v = (t.classification_values && t.classification_values[0]) || '';
          if (v && NAMED_DIVISION_OPTIONS.includes(v)) {
            setNamedDivisionChoice(v);
            setNamedDivisionOtherText('');
          } else if (v) {
            setNamedDivisionChoice(NAMED_DIVISION_OTHER);
            setNamedDivisionOtherText(v);
          } else {
            setNamedDivisionChoice('');
            setNamedDivisionOtherText('');
          }
          setClassificationValues([]);
        } else {
          setClassificationValues(t.classification_values || []);
          setNamedDivisionChoice('');
          setNamedDivisionOtherText('');
        }

        const pps = t.players_per_side;
        if (pps === null || pps === undefined) {
          setFormatChoice('');
          setFormatOtherText('');
        } else if (FORMAT_OPTIONS.some(o => o.value === pps)) {
          setFormatChoice(String(pps));
          setFormatOtherText('');
        } else {
          setFormatChoice('other');
          setFormatOtherText(String(pps));
        }

        setFootballType(t.football_type || '');

        setOriginalClassification({
          type,
          values: t.classification_values || [],
          playersPerSide: pps ?? null,
          footballType: t.football_type || null,
        });
      })
      .catch(e => setLoadError(e.message))
      .finally(() => setLoading(false));
  }, [teamId]);

  // Best-effort, independent of the load above — a slug-lookup failure
  // must never block Team Settings itself; it just means the "View
  // Public Page" link doesn't render.
  useEffect(() => {
    if (!team?.program_id) { setProgramSlug(null); return; }
    let cancelled = false;
    getProgram(team.program_id).then(p => { if (!cancelled) setProgramSlug(p?.slug || null); }).catch(() => { if (!cancelled) setProgramSlug(null); });
    return () => { cancelled = true; };
  }, [team?.program_id]);

  // Resolves the current classification/format picker state into the
  // actual values that would be submitted, without mutating state — same
  // shape as CreateSeasonTeamPage's own resolvers.
  const resolveClassificationValues = () => {
    if (classificationType === 'named_division') {
      if (namedDivisionChoice === NAMED_DIVISION_OTHER) {
        const custom = namedDivisionOtherText.trim();
        return custom ? [custom] : [];
      }
      return namedDivisionChoice ? [namedDivisionChoice] : [];
    }
    return classificationValues;
  };

  const resolvePlayersPerSide = () => {
    if (formatChoice === 'other') {
      const n = parseInt(formatOtherText, 10);
      return Number.isFinite(n) ? n : null;
    }
    if (formatChoice === '') return null;
    const n = parseInt(formatChoice, 10);
    return Number.isFinite(n) ? n : null;
  };

  const resolvedClassificationValues = resolveClassificationValues();
  const resolvedPlayersPerSide = resolvePlayersPerSide();

  const isClassificationDirty = useMemo(() => {
    if (!originalClassification) return false;
    return (
      classificationType !== originalClassification.type
      || resolvedPlayersPerSide !== originalClassification.playersPerSide
      || (footballType || null) !== originalClassification.footballType
      || JSON.stringify(resolvedClassificationValues) !== JSON.stringify(originalClassification.values)
    );
  }, [originalClassification, classificationType, resolvedClassificationValues, resolvedPlayersPerSide, footballType]);

  const canEditClassification = Boolean(
    team && hasProgramAuthority && (team.season_status === 'setup' || team.season_status === 'preseason')
  );

  // Once a Team has structured classification, its legacy grade_age_group
  // becomes an automatically-maintained compatibility mirror (see
  // update_team_classification()'s own comments, 028) — the ordinary
  // Grade / age group text field is no longer shown or independently
  // editable, so it can't drift out of sync with the structured section
  // below. A LEGACY Team (classification_type still NULL) keeps that
  // field exactly as before — never auto-migrated into structured.
  const isStructured = Boolean(team?.classification_type);

  const handleModeChange = (_e, mode) => {
    if (mode === null) return;
    setClassificationType(mode);
    setClassificationValues([]);
    setNamedDivisionChoice('');
    setNamedDivisionOtherText('');
  };

  const doSaveClassification = async () => {
    setClassificationSaving(true);
    setClassificationError(null);
    try {
      // Same formatter creation uses — keeps the legacy grade_age_group
      // display string synchronized in the SAME backend call, so
      // guardian/join-code preview surfaces (still reading grade_age_group
      // directly) never disagree with the structured classification.
      const gradeAgeGroup = formatTeamClassification(classificationType, resolvedClassificationValues);
      const updated = await updateTeamClassificationAndFootballType({
        teamId,
        classificationType,
        classificationValues: resolvedClassificationValues,
        playersPerSide: resolvedPlayersPerSide,
        footballType: footballType || null,
        gradeAgeGroup,
      });
      setTeam(updated);
      setOriginalClassification({
        type: classificationType,
        values: resolvedClassificationValues,
        playersPerSide: resolvedPlayersPerSide,
        footballType: footballType || null,
      });
      setClassificationConfirmOpen(false);
    } catch (e) {
      setClassificationError(e.message || 'Could not update classification.');
    } finally {
      setClassificationSaving(false);
    }
  };

  const handleSaveClassificationClick = () => {
    setClassificationError(null);
    if (classificationType) {
      if (resolvedClassificationValues.length === 0) {
        setClassificationError('Select at least one value, or switch off classification entirely.');
        return;
      }
      if (classificationType === 'named_division' && resolvedClassificationValues.length !== 1) {
        setClassificationError('Named Division allows exactly one value.');
        return;
      }
    }
    if (resolvedPlayersPerSide !== null && (resolvedPlayersPerSide < 4 || resolvedPlayersPerSide > 15)) {
      setClassificationError('Players per side must be between 4 and 15.');
      return;
    }

    // Preseason requires one confirmation, and only because a material
    // value is actually changing (the Save button is already disabled
    // when nothing changed — see isClassificationDirty). Setup saves
    // immediately, matching the rest of this phase's lifecycle matrix.
    if (team.season_status === 'preseason') {
      setClassificationConfirmOpen(true);
    } else {
      doSaveClassification();
    }
  };

  const activeTeamAdmins = useMemo(
    () => staff.filter(s => s.role === 'team_admin' && s.status === 'active'),
    [staff]
  );

  // Whether the CURRENT viewer personally holds team_admin — distinct
  // from hasProgramAuthority above. Archive/Reactivate (028) preserves
  // this legacy path unchanged for anyone who holds it, alongside the new
  // Program-authority path for Owner/Admin who may not.
  const isTeamAdmin = useMemo(
    () => staff.some(s => s.user_id === currentUser?.id && s.role === 'team_admin' && s.status === 'active'),
    [staff, currentUser]
  );
  const canManageArchive = isTeamAdmin || hasProgramAuthority;

  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));

  const handleSave = async (e) => {
    e.preventDefault();
    setSaveError(null);
    setSaving(true);
    try {
      await updateTeam(teamId, {
        team_name: form.team_name,
        organization_name: form.organization_name || null,
        city: form.city,
        state: form.state,
        zip_code: form.zip_code,
        season: Number(form.season),
        // Structured Teams: grade_age_group is an automatically-maintained
        // mirror of structured classification (see update_team_
        // classification()) — the ordinary Save Changes form must never
        // independently overwrite it. Legacy Teams: unchanged, still the
        // one place this text lives.
        ...(isStructured ? {} : { grade_age_group: form.grade_age_group }),
      });
      navigate(`/my-teams/${teamId}`);
    } catch (e2) {
      setSaveError(e2.message || 'Could not save changes.');
    } finally {
      setSaving(false);
    }
  };

  // Routes through whichever authority path the CURRENT viewer actually
  // holds — the pre-existing plain updateTeam() toggle for anyone with
  // team_admin (unchanged, zero risk of regressing someone who already
  // relies on it), or the new archive_team()/reactivate_team() RPCs (028)
  // for Program Owner/Admin who don't personally hold team_admin. Never
  // tried in the other order — a co-Team-Admin with no Program authority
  // must keep working exactly as before.
  const performStatusChange = (nextStatus) => {
    if (isTeamAdmin) return updateTeam(teamId, { status: nextStatus });
    return nextStatus === 'archived' ? archiveTeam(teamId) : reactivateTeam(teamId);
  };

  const handleArchiveConfirm = async () => {
    setArchiveError(null);
    setArchiving(true);
    try {
      const updated = await performStatusChange('archived');
      setTeam(updated);
      setArchiveConfirmOpen(false);
    } catch (e2) {
      setArchiveError(e2.message || 'Could not archive this team.');
    } finally {
      setArchiving(false);
    }
  };

  const handleReactivate = async () => {
    setArchiveError(null);
    setArchiving(true);
    try {
      const updated = await performStatusChange('active');
      setTeam(updated);
    } catch (e2) {
      setArchiveError(e2.message || 'Could not reactivate this team.');
    } finally {
      setArchiving(false);
    }
  };

  // Archive requires exactly one confirmation; Reactivate requires none.
  const handleArchiveButtonClick = () => {
    setArchiveError(null);
    if (team.status === 'archived') {
      handleReactivate();
    } else {
      setArchiveConfirmOpen(true);
    }
  };

  const handleTogglePublicPage = async () => {
    setPublicPageError(null);
    setPublicPageSaving(true);
    try {
      const updated = await updateTeamPublicPageSettings({
        teamId, publicPageEnabled: !team.public_page_enabled, slug: null,
      });
      setTeam(updated);
    } catch (e) {
      setPublicPageError(e.message || 'Could not update the public page setting.');
    } finally {
      setPublicPageSaving(false);
    }
  };

  const handleRevokeAdmin = async (row) => {
    setRevokeError(null);
    setRevokingId(row.id);
    try {
      await revokeTeamStaff(row.id);
      await loadStaff();
    } catch (e) {
      setRevokeError(e.message || 'Could not remove that Team Admin.');
    } finally {
      setRevokingId(null);
    }
  };

  if (loading) return <Box sx={{ py: 10, textAlign: 'center' }}><CircularProgress sx={{ color: '#2ea84b' }} /></Box>;
  if (loadError) return <Container maxWidth="sm" sx={{ py: 6 }}><Alert severity="error">{loadError}</Alert></Container>;
  if (!team || !form) return null;

  // A Program-created Season Team already has its organization identity
  // represented by teams.program_id (022) — organization_name was
  // auto-supplied from the Program's own name at creation time
  // (CreateSeasonTeamPage). Editing it here would let it silently drift
  // out of sync with the Program with no indication anything unusual
  // happened, so it's shown as plain context instead of an editable
  // field. Standalone (non-Program) teams keep it fully editable — that
  // column is the ONLY place their organization identity lives.
  const isProgramLinked = Boolean(team.program_id);

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Button onClick={() => navigate(`/my-teams/${teamId}`)} sx={{ textTransform: 'none', color: '#6a8aa8', mb: 1, pl: 0 }}>
        ← {team.team_name}
      </Button>
      <Typography variant="h4" sx={{ fontWeight: 900, mb: 3 }}>Team Settings</Typography>

      <Box component="form" onSubmit={handleSave}>
        <Grid container spacing={2.5}>
          <Grid item xs={12}>
            <TextField fullWidth label="Team name" value={form.team_name} onChange={set('team_name')} required />
          </Grid>
          <Grid item xs={12}>
            {isProgramLinked ? (
              <Box>
                <Typography sx={{ color: '#6a8aa8', fontSize: '0.75rem', mb: 0.25 }}>Organization / School</Typography>
                <Typography sx={{ fontWeight: 600 }}>{form.organization_name || '—'}</Typography>
                <Typography sx={{ color: '#4a6080', fontSize: '0.75rem', mt: 0.25 }}>
                  Set automatically from this Season Team's Program when it was created.
                </Typography>
              </Box>
            ) : (
              <TextField fullWidth label="Organization / School" value={form.organization_name} onChange={set('organization_name')} placeholder="Optional" />
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="City" value={form.city} onChange={set('city')} required />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField fullWidth label="State" value={form.state} onChange={set('state')} required inputProps={{ maxLength: 2 }} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField fullWidth label="ZIP code" value={form.zip_code} onChange={set('zip_code')} required />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth type="number" label="Season" value={form.season} onChange={set('season')} required inputProps={{ min: 2000, max: 2100, step: 1 }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            {isStructured ? (
              <Box>
                <Typography sx={{ color: '#6a8aa8', fontSize: '0.75rem', mb: 0.25 }}>Grade / age group</Typography>
                <Typography sx={{ fontWeight: 600 }}>{classificationDisplay(team) || '—'}</Typography>
                <Typography sx={{ color: '#4a6080', fontSize: '0.75rem', mt: 0.25 }}>
                  Maintained automatically from Team Classification & Format below.
                </Typography>
              </Box>
            ) : (
              <TextField fullWidth label="Grade / age group" value={form.grade_age_group} onChange={set('grade_age_group')} required />
            )}
          </Grid>
        </Grid>

        {saveError && <Alert severity="error" sx={{ mt: 3 }}>{saveError}</Alert>}

        <Box sx={{ display: 'flex', gap: 1.5, mt: 3, flexWrap: 'wrap' }}>
          <Button type="submit" variant="contained" color="primary" disabled={saving} size="large" sx={{ textTransform: 'none', fontWeight: 800, px: 4 }}>
            {saving ? <CircularProgress size={20} sx={{ color: '#050c16' }} /> : 'Save Changes'}
          </Button>
          <Button onClick={() => navigate(`/my-teams/${teamId}`)} disabled={saving} sx={{ textTransform: 'none', color: '#6a8aa8' }}>
            Cancel
          </Button>
        </Box>
      </Box>

      <Box sx={{ mt: 5, pt: 3, borderTop: '1px solid #1e2a3a' }}>
        <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>Team Classification & Format</Typography>
        <Typography sx={{ color: '#6a8aa8', fontSize: '0.85rem', mb: 2 }}>
          {canEditClassification
            ? (team.season_status === 'preseason'
              ? 'Changing this requires one confirmation, since the Season Team is already in Preseason.'
              : 'Freely editable while this Season Team is in Setup.')
            : 'Locked once a Season Team leaves Preseason. Only Program Owners/Admins can edit this, and only during Setup or Preseason.'}
        </Typography>

        {!canEditClassification ? (
          <Card sx={{ backgroundColor: 'rgba(10,20,34,0.8)', border: '1px solid #1e2a3a' }}>
            <CardContent>
              <Typography sx={{ color: '#6a8aa8', fontSize: '0.75rem' }}>Classification</Typography>
              <Typography sx={{ fontWeight: 700, mb: 1.5 }}>{classificationDisplay(team) || '—'}</Typography>
              {footballTypeDisplay(team.football_type) && (
                <>
                  <Typography sx={{ color: '#6a8aa8', fontSize: '0.75rem' }}>Football Type</Typography>
                  <Typography sx={{ fontWeight: 700, mb: 1.5 }}>{footballTypeDisplay(team.football_type)}</Typography>
                </>
              )}
              <Typography sx={{ color: '#6a8aa8', fontSize: '0.75rem' }}>Football Format</Typography>
              <Typography sx={{ fontWeight: 700 }}>{playersPerSideDisplay(team.players_per_side) || '—'}</Typography>
            </CardContent>
          </Card>
        ) : (
          <Card sx={{ backgroundColor: 'rgba(10,20,34,0.8)', border: '1px solid #1e2a3a' }}>
            <CardContent>
              <Typography sx={{ fontWeight: 700, mb: 1 }}>Classification</Typography>
              <ToggleButtonGroup
                value={classificationType} exclusive onChange={handleModeChange} size="small" sx={{ mb: 2, flexWrap: 'wrap' }}
              >
                {Object.keys(CLASSIFICATION_TYPE_LABELS).map(t => (
                  <ToggleButton key={t} value={t} sx={{ textTransform: 'none', fontWeight: 700 }}>
                    {CLASSIFICATION_TYPE_LABELS[t]}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>

              {classificationType && classificationType !== 'named_division' && (
                <MultiSelectClassification type={classificationType} values={classificationValues} onChange={setClassificationValues} />
              )}
              {classificationType === 'named_division' && (
                <NamedDivisionClassification
                  choice={namedDivisionChoice}
                  otherText={namedDivisionOtherText}
                  onChoiceChange={setNamedDivisionChoice}
                  onOtherTextChange={setNamedDivisionOtherText}
                />
              )}

              <Divider sx={{ my: 2.5 }} />

              <Typography sx={{ fontWeight: 700, mb: 1 }}>Football Type</Typography>
              <FootballTypePicker value={footballType} onChange={setFootballType} />

              <Divider sx={{ my: 2.5 }} />

              <Typography sx={{ fontWeight: 700, mb: 1 }}>Football Format</Typography>
              <FormatPicker
                choice={formatChoice}
                otherText={formatOtherText}
                onChoiceChange={setFormatChoice}
                onOtherTextChange={setFormatOtherText}
              />

              {classificationError && <Alert severity="error" sx={{ mt: 2 }}>{classificationError}</Alert>}

              <Button
                onClick={handleSaveClassificationClick}
                disabled={!isClassificationDirty || classificationSaving}
                variant="contained" color="primary"
                sx={{ mt: 2.5, textTransform: 'none', fontWeight: 700 }}
              >
                {classificationSaving ? <CircularProgress size={18} sx={{ color: '#050c16' }} /> : 'Save Classification & Format'}
              </Button>
            </CardContent>
          </Card>
        )}
      </Box>

      <Dialog
        open={classificationConfirmOpen}
        onClose={() => { if (!classificationSaving) setClassificationConfirmOpen(false); }}
        PaperProps={{ sx: { backgroundColor: 'rgba(10,20,34,0.98)', border: '1px solid #1e2a3a' } }}
      >
        <DialogTitle sx={{ fontWeight: 900 }}>Update Classification & Format?</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1.5 }}>
            This Season Team is already in Preseason. Confirm the change below:
          </Typography>
          <Typography sx={{ color: '#6a8aa8', fontSize: '0.75rem' }}>New classification</Typography>
          <Typography sx={{ fontWeight: 800, mb: 1.5 }}>
            {classificationDisplay({ classification_type: classificationType, classification_values: resolvedClassificationValues }) || '—'}
          </Typography>
          <Typography sx={{ color: '#6a8aa8', fontSize: '0.75rem' }}>New football type</Typography>
          <Typography sx={{ fontWeight: 800, mb: 1.5 }}>{footballTypeDisplay(footballType) || '—'}</Typography>
          <Typography sx={{ color: '#6a8aa8', fontSize: '0.75rem' }}>New football format</Typography>
          <Typography sx={{ fontWeight: 800 }}>{playersPerSideDisplay(resolvedPlayersPerSide) || '—'}</Typography>

          {classificationError && <Alert severity="error" sx={{ mt: 2 }}>{classificationError}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setClassificationConfirmOpen(false)} disabled={classificationSaving} sx={{ textTransform: 'none', color: '#9eb8d0' }}>
            Cancel
          </Button>
          <Button
            onClick={doSaveClassification}
            disabled={classificationSaving}
            variant="contained" color="primary"
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            {classificationSaving ? <CircularProgress size={18} sx={{ color: '#050c16' }} /> : 'Confirm Change'}
          </Button>
        </DialogActions>
      </Dialog>

      {hasProgramAuthority && (
        <Box sx={{ mt: 5, pt: 3, borderTop: '1px solid #1e2a3a' }}>
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>Public Team Page</Typography>
          <Typography sx={{ color: '#6a8aa8', fontSize: '0.85rem', mb: 2 }}>
            A Team existing here does not automatically get a public page — publish it only when you're ready for its schedule and results to be visible to anyone, without signing in.
          </Typography>
          <Card sx={{ backgroundColor: 'rgba(10,20,34,0.8)', border: '1px solid #1e2a3a' }}>
            <CardContent>
              {!team.division_id ? (
                <Typography sx={{ color: '#9eb8d0' }}>
                  This Team needs to be assigned to a Division (Seasons &amp; Divisions) before it can have a public page.
                </Typography>
              ) : (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
                    <Box>
                      <Typography sx={{ fontWeight: 700 }}>
                        {team.public_page_enabled ? 'Public page is live' : 'Public page is not published'}
                      </Typography>
                      {team.public_page_enabled && programSlug && (
                        <Typography
                          component="span"
                          onClick={() => navigate(`/programs/${programSlug}/teams/${teamId}`)}
                          sx={{ color: '#2ea84b', fontSize: '0.8rem', fontWeight: 700, cursor: 'pointer', textDecoration: 'underline' }}
                        >
                          View Public Page ↗
                        </Typography>
                      )}
                    </Box>
                    <Button
                      onClick={handleTogglePublicPage}
                      disabled={publicPageSaving}
                      variant={team.public_page_enabled ? 'outlined' : 'contained'}
                      color={team.public_page_enabled ? 'secondary' : 'primary'}
                      sx={{ textTransform: 'none', fontWeight: 700 }}
                    >
                      {publicPageSaving ? <CircularProgress size={18} sx={{ color: team.public_page_enabled ? undefined : '#050c16' }} /> : (team.public_page_enabled ? 'Unpublish Public Page' : 'Publish Public Team Page')}
                    </Button>
                  </Box>
                  {publicPageError && <Alert severity="error" sx={{ mt: 2 }}>{publicPageError}</Alert>}
                </>
              )}
            </CardContent>
          </Card>
        </Box>
      )}

      <Box sx={{ mt: 5, pt: 3, borderTop: '1px solid #1e2a3a' }}>
        <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>Team Administration</Typography>
        <Typography sx={{ color: '#6a8aa8', fontSize: '0.85rem', mb: 2 }}>
          Team Admins have full administrative control of this Season Team. This is separate from Head Coach / Assistant Coach, which are managed on the Staff page.
        </Typography>

        <Card sx={{ backgroundColor: 'rgba(10,20,34,0.8)', border: '1px solid #1e2a3a', mb: 2 }}>
          <CardContent>
            <Typography sx={{ fontWeight: 700, mb: 1.5 }}>Current Team Admins</Typography>
            <Divider sx={{ mb: 1.5 }} />
            {activeTeamAdmins.length === 0 ? (
              <Typography sx={{ color: '#6a8aa8' }}>No active Team Admins.</Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {activeTeamAdmins.map(row => {
                  const isLastAdmin = activeTeamAdmins.length <= 1;
                  return (
                    <Box key={row.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                      <Typography sx={{ fontWeight: 600 }}>{row.display_name || row.email}</Typography>
                      <Chip
                        size="small"
                        label={roleLabel('team_admin')}
                        onDelete={isLastAdmin ? undefined : () => handleRevokeAdmin(row)}
                        disabled={revokingId === row.id}
                        title={isLastAdmin ? 'The last Team Admin on a team cannot be removed' : undefined}
                        sx={{ bgcolor: '#0a2a14', color: '#2ea84b', fontWeight: 700 }}
                      />
                    </Box>
                  );
                })}
              </Box>
            )}
            {revokeError && <Alert severity="error" sx={{ mt: 1.5 }}>{revokeError}</Alert>}
          </CardContent>
        </Card>

        <Card sx={{ backgroundColor: 'rgba(10,20,34,0.8)', border: '1px solid #1e2a3a' }}>
          <CardContent>
            <Typography sx={{ fontWeight: 700, mb: 1.5 }}>Add Team Admin</Typography>
            <AddTeamAdminForm teamId={teamId} teamName={team.team_name} onAdded={loadStaff} />
          </CardContent>
        </Card>
      </Box>

      {canManageArchive && (
        <Box sx={{ mt: 5, pt: 3, borderTop: '1px solid #1e2a3a' }}>
          <Typography sx={{ fontWeight: 700, mb: 1 }}>{team.status === 'archived' ? 'Reactivate team' : 'Archive team'}</Typography>
          <Typography sx={{ color: '#6a8aa8', fontSize: '0.85rem', mb: 2 }}>
            {team.status === 'archived'
              ? 'This team is archived. Reactivating makes it visible/editable as normal again.'
              : 'Archiving keeps the team, its roster, staff, and history fully intact — it just marks the team inactive. This does not delete anything, and does not change its season or Program status.'}
          </Typography>
          {archiveError && <Alert severity="error" sx={{ mb: 2 }}>{archiveError}</Alert>}
          <Button
            onClick={handleArchiveButtonClick}
            disabled={archiving}
            variant="outlined" color={team.status === 'archived' ? 'primary' : 'secondary'}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            {archiving ? <CircularProgress size={18} /> : (team.status === 'archived' ? 'Reactivate Team' : 'Archive Team')}
          </Button>
        </Box>
      )}

      <Dialog
        open={archiveConfirmOpen}
        onClose={() => { if (!archiving) setArchiveConfirmOpen(false); }}
        PaperProps={{ sx: { backgroundColor: 'rgba(10,20,34,0.98)', border: '1px solid #1e2a3a' } }}
      >
        <DialogTitle sx={{ fontWeight: 900 }}>Archive Team?</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1.5 }}>
            {team.team_name} will be marked archived. Its roster, staff, and history are fully preserved — nothing is deleted, and its season status and Program are unaffected.
          </Typography>
          <Typography sx={{ color: '#9eb8d0' }}>
            You can reactivate it at any time.
          </Typography>
          {archiveError && <Alert severity="error" sx={{ mt: 2 }}>{archiveError}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setArchiveConfirmOpen(false)} disabled={archiving} sx={{ textTransform: 'none', color: '#9eb8d0' }}>
            Cancel
          </Button>
          <Button
            onClick={handleArchiveConfirm}
            disabled={archiving}
            variant="contained" color="error"
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            {archiving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Archive Team'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default function TeamSettingsPage() {
  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Navbar />
      <RequireAuth><TeamSettingsInner /></RequireAuth>
    </Box>
  );
}
